import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users, CheckCircle, Clock, Calendar, MapPin, RefreshCw, Search, X, XCircle } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { addNotification } from '../../store/slices/uiSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { webSocketService } from '../../services/websocketService';
import { logError } from '../../utils/errorLogger';
import Select from '../UI/Select';

interface Guest {
  id: number;
  guest: string;
  guest_name: string;
  guest_phone: string;
  response_status: string;
  attended: 'pending' | 'attended' | 'not_attended';
  attendance_confirmed_by: string | null;
  attendance_confirmed_by_name: string | null;
  attendance_confirmed_at: string | null;
  companion_count: number | null;
  companion_guest_events?: Array<{
    id: number | string;
    display_name: string;
    guest_name: string;
    status: string;
    attended?: 'pending' | 'attended' | 'not_attended';
    attendance_confirmed_by?: string | null;
    attendance_confirmed_by_name?: string | null;
    attendance_confirmed_at?: string | null;
  }>;
}

interface EventAttendanceManagementProps {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
}

// Attendance Dropdown Component (reused from usher page)
interface AttendanceDropdownProps {
  guestId: number;
  currentStatus: 'pending' | 'attended' | 'not_attended';
  onStatusChange: (guestId: number, attended: 'pending' | 'attended' | 'not_attended') => void;
  isUpdating: boolean;
}

const AttendanceDropdown: React.FC<AttendanceDropdownProps> = ({
  guestId,
  currentStatus,
  onStatusChange,
  isUpdating
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = 100; // Approximate height for 2 options
    const dropdownWidth = rect.width;
    
    let top = rect.bottom + 4;
    let left = rect.left;
    
    // Check if dropdown fits below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      // Show above
      top = rect.top - dropdownHeight - 4;
    }
    
    // Ensure dropdown doesn't go off screen horizontally
    if (left + dropdownWidth > viewportWidth - 8) {
      left = viewportWidth - dropdownWidth - 8;
    }
    if (left < 8) {
      left = 8;
    }
    
    // Ensure dropdown doesn't go off screen vertically
    if (top < 8) {
      top = 8;
    }
    if (top + dropdownHeight > viewportHeight - 8) {
      top = viewportHeight - dropdownHeight - 8;
    }
    
    setDropdownPosition({ top, left, width: dropdownWidth });
  };

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      
      // Recalculate on scroll and resize
      const handleUpdate = () => {
        if (isOpen) {
          calculatePosition();
        }
      };
      
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setDropdownPosition(null);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  const options: Array<{
    value: 'pending' | 'attended' | 'not_attended';
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }> = [
    { value: 'attended', label: 'Attended', icon: CheckCircle, color: 'text-green-400' },
    { value: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-400' },
    { value: 'not_attended', label: 'Not Attended', icon: XCircle, color: 'text-red-400' }
  ];

  const currentOption = options.find(opt => opt.value === currentStatus);

  const handleSelect = (attended: 'pending' | 'attended' | 'not_attended') => {
    onStatusChange(guestId, attended);
    setIsOpen(false);
    setDropdownPosition(null);
  };

  const getStatusButtonClasses = () => {
    if (currentStatus === 'attended') {
      return theme === 'dark'
        ? 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
        : 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100';
    } else if (currentStatus === 'pending') {
      return theme === 'dark'
        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30'
        : 'bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100';
    } else {
      return theme === 'dark'
        ? 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
        : 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100';
    }
  };

  const getDropdownClasses = () => {
    return theme === 'dark'
      ? 'bg-gray-800 border-gray-700 shadow-lg'
      : 'bg-white border-gray-200 shadow-lg';
  };

  const getDropdownItemClasses = (isSelected: boolean) => {
    if (isSelected) {
      return theme === 'dark'
        ? 'bg-gray-700/50'
        : 'bg-gray-100';
    }
    return theme === 'dark'
      ? 'hover:bg-gray-700/50'
      : 'hover:bg-gray-50';
  };

  const getOptionColor = (optionValue: string) => {
    if (optionValue === 'attended') {
      return theme === 'dark' ? 'text-green-400' : 'text-green-700';
    } else if (optionValue === 'pending') {
      return theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700';
    } else {
      return theme === 'dark' ? 'text-red-400' : 'text-red-700';
    }
  };

  return (
    <>
      <div className="relative attendance-dropdown">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          disabled={isUpdating}
          className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-300 border shadow-sm ${getStatusButtonClasses()} ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
        >
          {isUpdating ? (
            <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              {currentOption && <currentOption.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />}
              <span className="whitespace-nowrap">{currentOption?.label}</span>
              <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </button>
      </div>

      {/* Dropdown Portal */}
      {isOpen && !isUpdating && dropdownPosition && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed backdrop-blur-sm border rounded-lg z-[10001] shadow-lg ${getDropdownClasses()}`}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          {options.map((option) => (
            <button
              key={option.value.toString()}
              onClick={() => handleSelect(option.value)}
              className={`w-full flex items-center space-x-2 px-4 py-2 text-left transition-colors ${getDropdownItemClasses(option.value === currentStatus)}`}
            >
              <option.icon className={`h-4 w-4 ${getOptionColor(option.value)}`} />
              <span className={getOptionColor(option.value)}>{option.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const EventAttendanceManagement: React.FC<EventAttendanceManagementProps> = ({
  eventId,
  eventTitle,
  eventDate,
  eventTime,
  eventLocation
}) => {
  const dispatch = useAppDispatch();
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState<'all' | 'attended' | 'pending' | 'not_attended'>('all');
  const [responseStatusFilter, setResponseStatusFilter] = useState<string>('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(21);

  const fetchEventGuests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/events/${eventId}/guests/`, {
        params: {
          page: currentPage,
          page_size: pageSize
        }
      });
      
      // Transform the guest data to match our interface
      const guestsData = (response.data.results || []).map((guest: any) => ({
        id: guest.id,
        guest: guest.guest?.id || '',
        // Use guest_name from API (now includes nickname if available), fallback to constructing from first/last name
        guest_name: guest.guest_name || `${guest.guest?.first_name || ''} ${guest.guest?.last_name || ''}`.trim() || 'Unknown Guest',
        guest_phone: guest.guest?.phone || '',
        response_status: guest.status || 'pending',
        attended: guest.attended || 'pending',
        attendance_confirmed_by: guest.attendance_confirmed_by || null,
        attendance_confirmed_by_name: guest.attendance_confirmed_by_name || null,
        attendance_confirmed_at: guest.attendance_confirmed_at || null,
        companion_count: guest.companion_count || null,
        companion_guest_events: guest.companion_guest_events || []
      }));
      setGuests(guestsData);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
      
    } catch (error: any) {
      logError('Error fetching event guests', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load guests. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  }, [eventId, dispatch, currentPage, pageSize]);

  useEffect(() => {
    fetchEventGuests();
    
    // Subscribe to real-time updates
    const unsubscribe = webSocketService.on('guest_response', (data: any) => {
      if (data.data && data.data.type === 'attendance_update') {
        const guestEventId = parseInt(data.data.guest_event_id);
        setGuests(prevGuests => {
          return prevGuests.map(guest => {
            // Check if this is the main guest
            if (guest.id === guestEventId) {
              return { 
                ...guest, 
                attended: data.data.attended,
                attendance_confirmed_at: data.data.attendance_confirmed_at,
                attendance_confirmed_by: data.data.attendance_confirmed_by,
                attendance_confirmed_by_name: data.data.attendance_confirmed_by_name || data.data.confirmed_by_name || null
              };
            }
            
            // Check if this is a companion of this guest
            if (guest.companion_guest_events && guest.companion_guest_events.some(c => Number(c.id) === guestEventId)) {
              return {
                ...guest,
                companion_guest_events: guest.companion_guest_events.map(companion => 
                  Number(companion.id) === guestEventId
                    ? {
                        ...companion,
                        attended: data.data.attended,
                        attendance_confirmed_at: data.data.attendance_confirmed_at,
                        attendance_confirmed_by: data.data.attendance_confirmed_by,
                        attendance_confirmed_by_name: data.data.attendance_confirmed_by_name || data.data.confirmed_by_name || null
                      }
                    : companion
                )
              };
            }
            
            return guest;
          });
        });
        
        // UI will be updated directly - no toast notification needed
      }
    });

    // Subscribe to WebSocket errors
    const unsubscribeError = webSocketService.on('websocket_error', (error: any) => {
      logError('Admin WebSocket error', error);
      const errorMessage = error.message || 'Unable to subscribe to real-time updates. Some features may not work.';
      
      // Only show notification for subscription-related errors, not connection errors
      if (errorMessage.includes('subscribe') || errorMessage.includes('Access denied') || errorMessage.includes('Event not found')) {
        dispatch(addNotification({
          type: 'error',
          title: 'Subscription Failed',
          message: errorMessage,
        }));
      }
    });

    // Subscribe to subscription confirmation
    const unsubscribeSubscriptionConfirmed = webSocketService.on('subscription_confirmed', (data: any) => {
      // Subscription confirmed
      // Optionally show success notification (commented out to avoid noise)
      // dispatch(addNotification({
      //   type: 'success',
      //   title: 'Connected',
      //   message: `Real-time updates enabled for ${data.event_title || 'event'}`,
      // }));
    });

      // Subscribe to the specific event
      // Ensure WebSocket is connected before subscribing
      let unsubscribeConnectionStatus: (() => void) | null = null;
      
      if (!webSocketService.connectionStatus) {
        webSocketService.connect().catch(error => {
          // Error is already logged in connect
        });
        
        // Listen for connection status to subscribe when ready
        const connectionStatusHandler = (data: { connected: boolean }) => {
          if (data.connected) {
            webSocketService.subscribeToEvent(eventId);
            if (unsubscribeConnectionStatus) {
              unsubscribeConnectionStatus();
              unsubscribeConnectionStatus = null;
            }
          }
        };
        
        unsubscribeConnectionStatus = webSocketService.on('connection_status', connectionStatusHandler);
        
        // Also subscribe immediately (will queue if not connected)
        webSocketService.subscribeToEvent(eventId);
      } else {
        webSocketService.subscribeToEvent(eventId);
      }

    return () => {
      unsubscribe();
      unsubscribeError();
      unsubscribeSubscriptionConfirmed();
      if (unsubscribeConnectionStatus) {
        unsubscribeConnectionStatus();
      }
    };
  }, [eventId, dispatch, fetchEventGuests]);

  const handleAttendanceToggle = async (guestEventId: number, attended: 'pending' | 'attended' | 'not_attended') => {
    try {
      setUpdating(guestEventId);
      const response = await apiClient.patch(`/events/${eventId}/guests/${guestEventId}/attendance/`, {
        attended
      });
      
      if (response.data.success) {
        // Update local state - use response data if available, otherwise use current user info
        const updatedGuest = response.data.data || response.data;
        const currentUserName = updatedGuest?.attendance_confirmed_by_name || 
                               updatedGuest?.confirmed_by_name || 
                               (user?.get_full_name || user?.first_name || 'Admin/Host');
        
        setGuests(prevGuests => 
          prevGuests.map(guest => {
            // Check if this is the main guest
            if (guest.id === guestEventId) {
              return { 
                ...guest, 
                attended, 
                attendance_confirmed_at: updatedGuest?.attendance_confirmed_at || new Date().toISOString(),
                attendance_confirmed_by: updatedGuest?.attendance_confirmed_by || user?.id || null,
                attendance_confirmed_by_name: currentUserName
              };
            }
            
            // Check if this is a companion of this guest
            if (guest.companion_guest_events && guest.companion_guest_events.some(c => Number(c.id) === guestEventId)) {
              return {
                ...guest,
                companion_guest_events: guest.companion_guest_events.map(companion => 
                  Number(companion.id) === guestEventId
                    ? {
                        ...companion,
                        attended,
                        attendance_confirmed_at: updatedGuest?.attendance_confirmed_at || new Date().toISOString(),
                        attendance_confirmed_by: updatedGuest?.attendance_confirmed_by || user?.id || null,
                        attendance_confirmed_by_name: currentUserName
                      }
                    : companion
                )
              };
            }
            
            return guest;
          })
        );
        
        // Note: Real-time updates will be handled by the WebSocket service
        // when other users make changes to the same event
        // UI will be updated directly - no toast notification needed
      }
    } catch (error: any) {
      logError('Error updating attendance', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to update attendance. Please try again.',
      }));
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getResponseStatusColor = (status: string) => {
    const isDark = theme === 'dark';
    switch (status) {
      case 'accepted':
        return isDark
          ? 'bg-green-500/20 text-green-400 border-green-500/30'
          : 'bg-green-50 text-green-700 border-green-300';
      case 'rejected':
        return isDark
          ? 'bg-red-500/20 text-red-400 border-red-500/30'
          : 'bg-red-50 text-red-700 border-red-300';
      case 'pending':
        return isDark
          ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
          : 'bg-yellow-50 text-yellow-700 border-yellow-300';
      case 'ignored':
        return isDark
          ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          : 'bg-gray-100 text-gray-700 border-gray-300';
      default:
        return isDark
          ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          : 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Filter guests based on search and filters
  const filteredGuests = guests.filter(guest => {
    // Search filter
    const matchesSearch = !searchTerm || 
      guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.guest_phone.includes(searchTerm) ||
      (guest.companion_guest_events && guest.companion_guest_events.some(c => 
        (c.display_name || c.guest_name).toLowerCase().includes(searchTerm.toLowerCase())
      ));
    
    // Attendance filter
    const matchesAttendance = attendanceFilter === 'all' ||
      (attendanceFilter === 'attended' && guest.attended === 'attended') ||
      (attendanceFilter === 'pending' && guest.attended === 'pending') ||
      (attendanceFilter === 'not_attended' && guest.attended === 'not_attended');
    
    // Response status filter
    const matchesResponseStatus = responseStatusFilter === 'all' ||
      guest.response_status === responseStatusFilter;
    
    return matchesSearch && matchesAttendance && matchesResponseStatus;
  });

  // Statistics based on all guests (not filtered)
  const attendedCount = guests.filter(guest => guest.attended === 'attended').length;
  const pendingCount = guests.filter(guest => guest.attended === 'pending').length;
  const notAttendedCount = guests.filter(guest => guest.attended === 'not_attended').length;
  const totalGuests = guests.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className={`w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-4 ${
            theme === 'dark' 
              ? 'border-white/30 border-t-white' 
              : 'border-gray-300 border-t-gray-600'
          }`}></div>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Event Info */}
      <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
        theme === 'dark' 
          ? 'bg-white/5 border-white/10' 
          : 'bg-white border-gray-200 shadow-sm'
      }`}>
        <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>{eventTitle}</h1>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div className={`flex items-center space-x-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="truncate">{formatDate(eventDate)}</span>
          </div>
          
          <div className={`flex items-center space-x-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            <span>{formatTime(eventTime)}</span>
          </div>
          
          <div className={`flex items-center space-x-2 sm:col-span-2 md:col-span-1 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="truncate">{eventLocation}</span>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 space-y-4 ${
        theme === 'dark' 
          ? 'bg-white/5 border-white/10' 
          : 'bg-white border-gray-200 shadow-sm'
      }`}>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search by name, phone, or companion..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-10 py-2.5 sm:py-3 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                theme === 'dark'
                  ? 'border-gray-600 bg-gray-700/50 text-white placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                  theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Attendance Filter */}
            <div className="w-full sm:w-auto">
              <Select
                options={[
                  { value: 'all', label: 'All Attendance' },
                  { value: 'attended', label: 'Attended' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'not_attended', label: 'Not Attended' }
                ]}
                value={attendanceFilter}
                onChange={(value) => setAttendanceFilter(value as 'all' | 'attended' | 'pending' | 'not_attended')}
                placeholder="All Attendance"
                className="w-full sm:w-[180px]"
              />
            </div>

            {/* Response Status Filter */}
            <div className="w-full sm:w-auto">
              <Select
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'accepted', label: 'Accepted' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'ignored', label: 'Ignored' }
                ]}
                value={responseStatusFilter}
                onChange={(value) => setResponseStatusFilter(value)}
                placeholder="All Status"
                className="w-full sm:w-[180px]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
          theme === 'dark' 
            ? 'bg-white/5 border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex items-center">
            <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${
              theme === 'dark' 
                ? 'bg-blue-500/20' 
                : 'bg-blue-50'
            }`}>
              <Users className={`h-5 w-5 sm:h-6 sm:w-6 ${
                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
              }`} />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>Total Guests</p>
              <p className={`text-xl sm:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{totalGuests}</p>
            </div>
          </div>
        </div>

        <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
          theme === 'dark' 
            ? 'bg-white/5 border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex items-center">
            <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${
              theme === 'dark' 
                ? 'bg-green-500/20' 
                : 'bg-green-50'
            }`}>
              <CheckCircle className={`h-5 w-5 sm:h-6 sm:w-6 ${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`} />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>Attended</p>
              <p className={`text-xl sm:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{attendedCount}</p>
            </div>
          </div>
        </div>

        <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
          theme === 'dark' 
            ? 'bg-white/5 border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex items-center">
            <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${
              theme === 'dark' 
                ? 'bg-yellow-500/20' 
                : 'bg-yellow-50'
            }`}>
              <Clock className={`h-5 w-5 sm:h-6 sm:w-6 ${
                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`} />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>Pending</p>
              <p className={`text-xl sm:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{pendingCount}</p>
            </div>
          </div>
        </div>

        <div className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 ${
          theme === 'dark' 
            ? 'bg-white/5 border-white/10' 
            : 'bg-white border-gray-200 shadow-sm'
        }`}>
          <div className="flex items-center">
            <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${
              theme === 'dark' 
                ? 'bg-red-500/20' 
                : 'bg-red-50'
            }`}>
              <XCircle className={`h-5 w-5 sm:h-6 sm:w-6 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-600'
              }`} />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>Not Attended</p>
              <p className={`text-xl sm:text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{notAttendedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Guests List */}
      <div className="space-y-4 sm:space-y-6">
        <h2 className={`text-xl sm:text-2xl font-bold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Guest Attendance</h2>
        
        {filteredGuests.length === 0 ? (
          <div className="text-center py-12">
            <Users className={`h-12 w-12 mx-auto mb-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <h3 className={`text-lg font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {guests.length === 0 ? 'No Guests Found' : 'No Guests Match Your Filters'}
            </h3>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              {guests.length === 0 
                ? 'This event doesn\'t have any guests yet.'
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4 overflow-visible">
            {filteredGuests.map((guest) => (
              <div
                key={guest.id}
                className={`backdrop-blur-sm border rounded-xl sm:rounded-2xl p-4 sm:p-6 relative overflow-visible ${
                  theme === 'dark' 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-white border-gray-200 shadow-sm'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className={`text-base sm:text-lg font-bold truncate ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>{guest.guest_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getResponseStatusColor(guest.response_status)}`}>
                        {guest.response_status ? guest.response_status.charAt(0).toUpperCase() + guest.response_status.slice(1) : 'Unknown'}
                      </span>
                    </div>
                    
                    <p className={`text-xs sm:text-sm mb-2 sm:mb-3 truncate ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>{guest.guest_phone}</p>
                    
                    {guest.companion_guest_events && guest.companion_guest_events.length > 0 && (
                      <div className={`space-y-2 mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <p className={`text-xs sm:text-sm font-medium ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Companion{guest.companion_guest_events.length > 1 ? 's' : ''}:
                        </p>
                        {guest.companion_guest_events.map((companion) => (
                          <div key={companion.id} className={`flex items-center justify-between p-2 rounded-lg ${
                            theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                          }`}>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs sm:text-sm truncate ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                {companion.display_name || companion.guest_name}
                              </p>
                              {companion.attendance_confirmed_at && (
                                <p className={`text-[10px] mt-1 ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                  Confirmed: {new Date(companion.attendance_confirmed_at).toLocaleString()}
                                  {companion.attendance_confirmed_by_name && ` by ${companion.attendance_confirmed_by_name}`}
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0 ml-2">
                              <AttendanceDropdown
                                guestId={Number(companion.id)}
                                currentStatus={companion.attended || 'pending'}
                                onStatusChange={handleAttendanceToggle}
                                isUpdating={updating === Number(companion.id)}
                              />
                            </div>
                          </div>
                        ))}
                        {guest.companion_count && guest.companion_count > guest.companion_guest_events.length && (
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{guest.companion_count - guest.companion_guest_events.length} empty companion{guest.companion_count - guest.companion_guest_events.length > 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {guest.attendance_confirmed_at && (
                      <p className={`text-[10px] sm:text-xs mt-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Confirmed at: {new Date(guest.attendance_confirmed_at).toLocaleString()}
                        {guest.attendance_confirmed_by_name && ` by ${guest.attendance_confirmed_by_name}`}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 sm:ml-4">
                    <div className="flex items-center justify-start sm:justify-end">
                      <AttendanceDropdown
                        guestId={guest.id}
                        currentStatus={guest.attended}
                        onStatusChange={handleAttendanceToggle}
                        isUpdating={updating === guest.id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className={`backdrop-blur-sm border rounded-xl p-4 mt-4 ${
            theme === 'dark' 
              ? 'bg-white/5 border-white/10' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} guests
                {(searchTerm || attendanceFilter !== 'all' || responseStatusFilter !== 'all') && (
                  <span className="ml-2">
                    ({filteredGuests.length} filtered)
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    currentPage === 1
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-white/10 text-white hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  First
                </button>
                
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    currentPage === 1
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-white/10 text-white hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? theme === 'dark'
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-600 text-white'
                            : theme === 'dark'
                              ? 'bg-white/10 text-gray-300 hover:bg-white/20'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    currentPage === totalPages
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-white/10 text-white hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Next
                </button>
                
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    currentPage === totalPages
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-white/10 text-white hover:bg-white/20'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventAttendanceManagement;
